import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import useSWR from 'swr';
import { useDebounceValue } from 'usehooks-ts';

import { dateToDateString } from '~/components/utils/dateUtils';
import { GlobalFilterContext, useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { fetchTicketsFilter } from '~/data/hooks/fetchTicketsFilter';
import type {
  API_TicketFilterSearchParams,
  EntryTypeKey,
  Ticket,
  TicketWithPortfolio,
} from '~/data/schemas/ticket';
import { mergeFilterModel, useGridFilterModel } from '~/hooks/useGridFilterModel';

const ticketSearchParam = 'ticketId';

/**
 * The query params this page turns into filters. `buildTicketParams` reads `car_id`,
 * `propertyName` and `number` back out of the URL when it fetches, so Clear Filters has to
 * take these with it or the request stays narrowed while the table shows no filters.
 */
const URL_FILTER_PARAMS = ['ticketId', 'entry_type', 'car_id', 'number', 'propertyName'];

export const getFilterModelFromQueryParams = (searchParams: URLSearchParams): GridFilterModel => {
  const ticketId = searchParams.get(ticketSearchParam);
  const entryType = searchParams.getAll('entry_type') as EntryTypeKey[];
  const carId = searchParams.get('car_id');
  const number = searchParams.get('number');
  const propertyName = searchParams.get('propertyName');
  const items: GridFilterModel['items'] = [];

  if (ticketId) {
    items.push({
      id: 'id',
      field: 'id',
      value: ticketId,
      operator: 'equals',
    });
  }

  if (entryType.length > 0) {
    items.push({
      id: 'entryType',
      field: 'entryType',
      value: entryType,
      operator: 'isAnyOf',
    });
  }

  if (carId) {
    items.push({
      id: 'carId',
      field: 'carId',
      operator: 'equals',
      value: carId,
    });
  }

  if (number) {
    items.push({
      id: 'number',
      field: 'number',
      operator: 'contains',
      value: number,
    });
  }

  if (propertyName) {
    items.push({
      id: 'property',
      field: 'property',
      operator: 'contains',
      value: propertyName,
    });
  }

  return { items };
};

const mapTicketToTicketWithPortfolio = (ticket: Ticket): TicketWithPortfolio => {
  return {
    ...ticket,
    // Flat the portfolio to be used by the table. `property` is null when the
    // ticket's Property was deleted (a SET_NULL FK), and its portfolio is
    // nullable too, so carry null through rather than assume (AMP-585/AMP-593).
    portfolio: ticket.property?.portfolio ?? null,
  } as TicketWithPortfolio;
};

type BuildFilterParamsOpts = {
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
  globalFilters: GlobalFilterContext['selectedFilters'];
  filterModel: GridFilterModel;
  searchParams: URLSearchParams;
  propertyId?: string;
};

/**
 * The columns `TicketFilter.ordering` accepts. Sorting is server-side, and
 * `OrderingFilter` is a `ChoiceFilter`: an unregistered value fails form
 * validation, which `DjangoFilterBackend` (`raise_exception = True`) turns into a
 * 400 for the whole request. Anything outside this set is dropped rather than
 * sent, so a column added later degrades to "no sort" instead of a broken page.
 */
const TICKET_ORDERING_FIELDS = new Set([
  'number',
  'startedTime',
  'entryType',
  'property',
  'serviceProviderGroup',
  'portfolio',
  'invoice',
]);

/** The grid's sort model as the endpoint's `ordering` param, or `undefined` for none. */
export const ticketOrderingParam = (sortModel: GridSortModel): string | undefined => {
  const ordering = sortModel
    .filter((item) => TICKET_ORDERING_FIELDS.has(item.field))
    .map((item) => `${item.sort === 'desc' ? '-' : ''}${item.field}`);
  return ordering.length > 0 ? ordering.join(',') : undefined;
};

export const buildFilterParams = ({
  page,
  pageSize,
  sortModel,
  globalFilters,
  filterModel,
  searchParams,
  propertyId,
}: BuildFilterParamsOpts): API_TicketFilterSearchParams | null => {
  // NOTE: MUI DataGrid passes odd values sometimes while loading. This is a safety check.
  if (pageSize <= 0 || page < 0) {
    return null;
  }

  const entryTypes: EntryTypeKey[] | undefined = filterModel.items
    .filter((item) => item.field === 'entryType')
    .map((item) => item.value)
    .at(0);

  // Filtering is server-side, so a column's Filter menu entry only does anything if
  // it is read here. Ticket # and Property were left out, which made both filters
  // look applied and change nothing. The URL params stay as the fallback, for the
  // links that arrive pre-filtered.
  const filterValue = (field: string): string | undefined => {
    const value = filterModel.items.find((item) => item.field === field)?.value;
    return typeof value === 'string' && value !== '' ? value : undefined;
  };

  const search = filterModel.quickFilterValues?.join(' ');

  return {
    limit: pageSize,
    offset: page * pageSize,
    ordering: ticketOrderingParam(sortModel) ?? '-startedTime',
    search: search ?? undefined,
    property: propertyId ?? undefined,
    carId: searchParams.get('car_id') ?? undefined,
    propertyName: filterValue('property') ?? searchParams.get('propertyName') ?? undefined,
    number: filterValue('number') ?? searchParams.get('number') ?? undefined,
    entryType: entryTypes,
    portfolios: globalFilters.portfolios,
    serviceProviders: globalFilters.serviceProviders,
    regions: globalFilters.regions,
    startedTimeAfter: dateToDateString(globalFilters.date.start) ?? undefined,
    startedTimeBefore: dateToDateString(globalFilters.date.end) ?? undefined,
  };
};

const ticketsFetcher = async ([_, params]: readonly [
  'ticket-table',
  API_TicketFilterSearchParams,
]) => {
  return await fetchTicketsFilter(params);
};

const defaultSortModel: GridSortModel = [{ field: 'startedTime', sort: 'desc' }];

type UseTicketsTableModelOpts = {
  initialFilterModel?: GridFilterModel;
};

export function useTicketsTableModel({ initialFilterModel }: UseTicketsTableModelOpts = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const filterModelFromParams = getFilterModelFromQueryParams(searchParams);

  const { filterModel, setFilterModel, quickFilterProps } = useGridFilterModel({
    initialState: initialFilterModel
      ? mergeFilterModel(initialFilterModel, filterModelFromParams)
      : filterModelFromParams,
    quickFilterPlaceHolder: 'Search Tickets...',
  });

  const clearUrlFilters = useCallback(
    () =>
      setSearchParams(
        (params) => {
          URL_FILTER_PARAMS.forEach((param) => params.delete(param));
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return {
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
    filterModel,
    setFilterModel,
    quickFilterProps,
    clearUrlFilters,
  };
}

export type UseTicketsOptions = {
  propertyId?: string;
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
};

export function useTicketsForTable({
  propertyId,
  paginationModel,
  sortModel,
  filterModel,
}: UseTicketsOptions) {
  const { selectedFilters } = useGlobalFilterContext();
  const [searchParams] = useSearchParams();
  const filterParams = useMemo(
    () =>
      buildFilterParams({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortModel,
        globalFilters: selectedFilters,
        filterModel,
        searchParams,
        propertyId,
      }),
    [paginationModel, sortModel, selectedFilters, filterModel, searchParams, propertyId],
  );

  const [filterParamsDebounced] = useDebounceValue(filterParams, 500);

  const onExport = useCallback(async () => {
    const { results } = await fetchTicketsFilter({
      ...filterParams,
      limit: undefined,
      offset: undefined,
    });
    return results.map((result) => mapTicketToTicketWithPortfolio(result));
  }, [filterParams]);

  const { data, isLoading } = useSWR(
    filterParamsDebounced ? (['ticket-table', filterParamsDebounced] as const) : null,
    ticketsFetcher,
  );

  const tickets = data?.results?.map((result) => mapTicketToTicketWithPortfolio(result)) ?? [];
  const numTickets = data?.metadata.numTickets ?? 0;
  const metadata = data?.metadata ?? null;

  return {
    tickets,
    numTickets,
    metadata,
    isLoading,
    onExport,
  };
}
